import { http } from "@/utils/http"
import { formatIsoDate } from "@/utils/common"
import SalesFetchApi from "@/utils/fetch/sales-fetch-api"
import { getPreviousBusinessPeriod } from "../utils/business-date"

const toFiniteNumber = (value) => {
    const number = Number(value)
    return Number.isFinite(number) ? number : 0
}

const calculateTrendPercentage = (currentValue, previousValue) => {
    const current = toFiniteNumber(currentValue)
    const previous = toFiniteNumber(previousValue)
    return previous > 0 ? ((current - previous) / previous) * 100 : 0
}

const formatTrendComparison = (value) => {
    const percentage = toFiniteNumber(value)
    return {
        percentage: percentage.toFixed(1),
        trend: percentage > 0 ? 'positive' :
            percentage < 0 ? 'negative' : 'neutral'
    }
}

const state = {
    pageTitle: "",
    loadingTopUsers: false,
    topUsers: [],
    showSidebar: true,
    dashboardStats: null,
    THEMES: {
        LIGHT: "LIGHT",
        DARK: "DARK",
    },
    theme: "",
    currencies: [],
    countries: [],
    states: [],
    cities: [],
    // attendance related data
    loadingAttendanceData: false,
    attendanceDate: formatIsoDate(),
    attendanceStatuses: [],
    attendanceHolidays: [],
    attendanceHolidayTypes: [],
    attendanceShifts: [],
    attendanceRecordsForDate: [],
    attendanceStats: null,
    warehouses: [],
    suppliers: [],
    productsForSelection: [],
    terpenesMaster: [],
    serverTime: "",
    serverDate: "",
    // gl account defaults.
    reasonCodes: [],
    modeOfPayments: [],
    itemGroups: [],
    glAccounts: [],
    glAccountTypes: [],
    GlAccountDefaultTypes: {},
    dateRanges: [],
    safeDrawerTransactionTypes: [],
    customerGroups: [],
    REPORT_MASTER_CODES: [],
    mastersFetchedAt: 0,
    mastersFetchedAtByScope: {},
    mastersCacheTtlMs: 5 * 60 * 1000,
    // other
    transferStatuses: {
        PENDING: "PENDING",
        SHIPPED: "SHIPPED",
        TRANSFERRED: "TRANSFERRED",
        COMPLETED: "COMPLETED",
    },
    warehouseRooms: {
        FRONT_ROOM: {
            title: "Front Room",
            id: "FRONT_ROOM",
        },
        BACK_ROOM: {
            title: "Back Room",
            id: "BACK_ROOM",
        },
    },
    yesNoList: [
        {
            id: 1,
            title: "Yes",
        },
        {
            id: 0,
            title: "No",
        },
    ],
    weightUoms: [
        {
            title: "Each",
        },
        {
            title: "Gram",
        },
    ],
    marijuanaProductType: [
        {
            title: "Seed",
        },
        {
            title: "Cannabis Oil",
        },
        {
            title: "Fresh Cannabis",
        },
        {
            title: "Flower",
        },
        {
            title: "Plant",
        },
        {
            title: "Edible Product",
        },
        {
            title: "Liquid Product",
        },
        {
            title: "Concentrate",
        },
    ],
    healthCanadaReportingCategory: [
        {
            title: "Seeds",
        },
        {
            title: "Plants",
        },
        {
            title: "Fresh Cannabis",
        },
        {
            title: "Dried Cannabis",
        },
        {
            title: "Edibles Solid",
        },
        {
            title: "Edibles Non-Solid",
        },
        {
            title: "Extracts Inhaled",
        },
        {
            title: "Extracts Ingested",
        },
        {
            title: "Extracts Other",
        },
        {
            title: "Topicals",
        },
    ],
}

const getters = {}

function normalizeMasterScopes(scope) {
    const normalized = Array.isArray(scope) ? scope : String(scope || "all").split(",")
    const scopes = normalized
        .map((item) => String(item).trim())
        .filter(Boolean)

    return scopes.length ? [...new Set(scopes)] : ["all"]
}

const mutations = {
    SET_DATA: (state, data) => {
        for (let key in data) {
            state[key] = data[key]
        }
    },
}

const actions = {
    setData(vuexContext, data) {
        vuexContext.commit("SET_DATA", data)
    },
    // Always initialize app in LIGHT theme and remove any persisted DARK state
    initTheme({ state }) {
        state.theme = state.THEMES.LIGHT
        try {
            localStorage.setItem("theme", state.THEMES.LIGHT)
        } catch (_) {}
        if (typeof document !== 'undefined') {
            const root = document.documentElement
            root.classList.remove('dark')
        }
    },
    // Future calls to setTheme will be forced to LIGHT until dark mode is reintroduced
    setTheme({ state }) {
        state.theme = state.THEMES.LIGHT
        try {
            localStorage.setItem("theme", state.THEMES.LIGHT)
        } catch (_) {}
        if (typeof document !== 'undefined') {
            const root = document.documentElement
            root.classList.remove('dark')
        }
    },
    async updateAttendanceDate({ state, dispatch }, date) {
        state.attendanceDate = date
        await dispatch("fetchRecordsByDate")
    },
    async fetchMainMasters({ state }, { force = false, scope = "all" } = {}) {
        const now = Date.now()
        const scopeKeys = normalizeMasterScopes(scope)
        const staleScopeKeys = scopeKeys.filter((scopeKey) => {
            const fetchedAt = state.mastersFetchedAtByScope[scopeKey] || (scopeKey === "all" ? state.mastersFetchedAt : 0)
            return force || !fetchedAt || now - fetchedAt >= state.mastersCacheTtlMs
        })

        if (!staleScopeKeys.length) {
            return
        }

        const requestScope = staleScopeKeys.includes("all") ? "all" : staleScopeKeys.join(",")
        const res = await http.get("/misc/masters", {
            params: {
                scope: requestScope,
            },
        })
        const result = res.data.result
        // store data returned for the requested scope without clearing unrelated state

        // Handle dashboard stats with proper property mapping
        let dashboardStats = result.dashboardStats || state.dashboardStats || {}
        
        // Make sure all required properties exist
        dashboardStats = {
            totalProducts: dashboardStats.totalProducts || 0,
            totalCustomers: dashboardStats.totalCustomers || 0,
            totalCrews: dashboardStats.totalCrews || 0,
            totalWarehouses: dashboardStats.totalWarehouses || 0,
            totalOrders: dashboardStats.totalOrders || 0,
            totalDrawers: dashboardStats.totalDrawers || 0,
            todayOrders: dashboardStats.todayOrders || dashboardStats.totalOrders || 0,
            todayRevenue: dashboardStats.todayRevenue || 0,
            averageOrderValue: dashboardStats.averageOrderValue || 0,
            openSafes: dashboardStats.openSafes || dashboardStats.totalDrawers || 0,
            openDrawers: dashboardStats.openDrawers || dashboardStats.totalDrawers || 0
        }
        
        if (result.dashboardStats) state.dashboardStats = dashboardStats
        if (result.currencies) state.currencies = result.currencies
        if (result.dateRanges) state.dateRanges = result.dateRanges
        if (result.REPORT_MASTER_CODES) state.REPORT_MASTER_CODES = result.REPORT_MASTER_CODES
        if (result.customerGroups) state.customerGroups = result.customerGroups
        if (result.productsForSelection) state.productsForSelection = result.productsForSelection
        if (result.glAccounts) state.glAccounts = result.glAccounts
        if (result.safeDrawerTransactionTypes) state.safeDrawerTransactionTypes = result.safeDrawerTransactionTypes

        if (result.reasonCodes) state.reasonCodes = result.reasonCodes
        if (result.modeOfPayments) state.modeOfPayments = result.modeOfPayments
        if (result.itemGroups) state.itemGroups = result.itemGroups
        if (result.GlAccountDefaultTypes) state.GlAccountDefaultTypes = result.GlAccountDefaultTypes
        if (result.glAccountTypes) state.glAccountTypes = result.glAccountTypes

        if (result.countries) state.countries = result.countries
        if (result.warehouses) state.warehouses = result.warehouses.map((item) => {
            // add title for select dropdown
            return { ...item, title: item.name }
        })

        if (result.terpenes) state.terpenesMaster = result.terpenes.map((item) => {
            // add title for select dropdown
            return { ...item, title: item.name }
        })
        if (result.suppliers) state.suppliers = result.suppliers
        if (result.itemGroups) state.itemGroups = result.itemGroups
        if (requestScope === "all") {
            state.mastersFetchedAt = now
        }

        state.mastersFetchedAtByScope = {
            ...state.mastersFetchedAtByScope,
            ...staleScopeKeys.reduce((acc, scopeKey) => {
                acc[scopeKey] = now
                return acc
            }, {}),
        }
    },
    async refreshDashboardStats({ state }, params = {}) {
        try {
            const startDateParam = params.startDate || params.dateRange?.startDate;
            const endDateParam = params.endDate || params.dateRange?.endDate;

            // Prepare API parameters for date filtering
            const queryParams = {};
            if (startDateParam) queryParams.startDate = startDateParam;
            if (endDateParam) queryParams.endDate = endDateParam;
            if (params.includePilotSales) queryParams.includePilotSales = true;
            
            // Calculate previous period dates for trend calculation
            if (params.comparisonPeriod === 'previous_period' && startDateParam && endDateParam) {
                const previousPeriod = getPreviousBusinessPeriod(startDateParam, endDateParam);

                if (previousPeriod) {
                    queryParams.prevStartDate = previousPeriod.start;
                    queryParams.prevEndDate = previousPeriod.end;
                }
            }
            
            // Call API with enhanced dashboard stats endpoint through the SalesFetchApi
            const response = await SalesFetchApi.getDashboardStats(queryParams);
            const result = response.result || {};
            
            // Get current period and trend data
            const currentPeriod = result.currentPeriod || {};
            const previousPeriod = result.previousPeriod || {};
            const trends = result.trends || {};
            const averageOrderValueTrend = trends.averageOrderValue !== undefined && trends.averageOrderValue !== null ?
                trends.averageOrderValue :
                calculateTrendPercentage(currentPeriod.averageOrderValue, previousPeriod.averageOrderValue);
            
            // Update dashboard stats
            let dashboardStats = { ...state.dashboardStats } || {};
            
            // Update with data from the enhanced API
            dashboardStats = {
                ...dashboardStats,
                totalProducts: currentPeriod.totalProducts || dashboardStats.totalProducts || 0,
                totalCustomers: currentPeriod.totalCustomers || dashboardStats.totalCustomers || 0,
                
                // Update orders to use the new currentPeriod.orders value
                todayOrders: currentPeriod.orders || 0,
                
                // Update revenue to use the new currentPeriod.revenue value
                todayRevenue: currentPeriod.revenue || 0,
                averageOrderValue: currentPeriod.averageOrderValue || 0
            };
            
            // Prepare comparison data for trends
            const comparisonData = {
                products: formatTrendComparison(trends.products),
                customers: formatTrendComparison(trends.customers),
                orders: formatTrendComparison(trends.orders),
                revenue: formatTrendComparison(trends.revenue),
                averageOrderValue: formatTrendComparison(averageOrderValueTrend)
            };
            
            // Return both the dashboard stats and comparison data
            return {
                dashboardStats,
                comparison: comparisonData,
                periods: {
                    current: currentPeriod,
                    previous: previousPeriod
                },
                error: false
            };
        } catch (error) {
            console.error("Error in refreshDashboardStats action:", error);
            return {
                dashboardStats: state.dashboardStats,
                comparison: {
                    products: { percentage: 0, trend: 'neutral' },
                    customers: { percentage: 0, trend: 'neutral' },
                    orders: { percentage: 0, trend: 'neutral' },
                    revenue: { percentage: 0, trend: 'neutral' },
                    averageOrderValue: { percentage: 0, trend: 'neutral' }
                },
                periods: {
                    current: {},
                    previous: {}
                },
                error: true
            };
        }
    },
    async updateServerTime() {
        try {
            const res = await http.get("/misc/server-time")
            const result = res.data.result
            state.serverTime = result.serverTime
            state.serverDate = result.serverDate
        } catch (err) {
            console.log("update error server time", err)
        }
    },
    async updateAttendanceStats({ state, dispatch }) {
        let totalPresent = 0
        let totalAbsent = 0
        let totalLeave = 0
        let totalOvertimeHours = 0

        for (let attendanceRecord of state.attendanceRecordsForDate) {
            const status = attendanceRecord.attendanceStatus

            if (status) {
                const stName = status.name.toLowerCase()
                if (stName.includes("present")) {
                    totalPresent++
                } else if (stName.includes("absent")) {
                    totalAbsent++
                } else if (stName.includes("leave")) {
                    totalLeave++
                }
            }
            totalOvertimeHours += attendanceRecord.overtimeHours || 0
        }
        state.attendanceStats = {
            totalPresent,
            totalAbsent,
            totalLeave,
            totalOvertimeHours,
        }
    },
    async fetchRecordsByDate({ state, dispatch }) {
        state.loadingAttendanceData = true
        try {
            const res = await http.get(`/attendance-records/filter?date=${state.attendanceDate}`)
            state.attendanceRecordsForDate = res.data.result
            dispatch("updateAttendanceStats")
        } catch (err) {
            console.log("error in loading dashboard stats", err)
        }
        state.loadingAttendanceData = false
    },
    async updateAttendanceRecord({ state, dispatch }, updateData) {
        try {
            const updateId = updateData.id
            delete updateData.id
            const res = await http.patch(`/attendance-records-crud/${updateId}`, updateData)
            const updatedResult = res.data.result

            // update the attendance records
            state.attendanceRecordsForDate = state.attendanceRecordsForDate.map((item) => {
                if (item.id === updateId) {
                    return updatedResult
                }
                return item
            })
            dispatch("updateAttendanceStats")
        } catch (err) {
            console.log("error in loading dashboard stats", err)
        }
    },
    async fetchAttendanceInfo({ state }) {
        state.loadingAttendanceData = true
        try {
            const res = await http.get("/misc/attendance-info")
            const result = res.data.result

            state.attendanceStatuses = result.attendanceStatuses
            state.attendanceHolidays = result.attendanceHolidays
            state.attendanceHolidayTypes = result.attendanceHolidayTypes
            state.attendanceShifts = result.attendanceShifts
        } catch (err) {
            console.log("error in loading dashboard stats", err)
        }
        state.loadingAttendanceData = false
    },
    async fetchStates({ state }, countryId) {
        try {
            const res = await http.get(`/misc/states?country_id=${countryId}`)
            state.states = res.data.result
        } catch (err) {
            console.log("error in loading dashboard states", err)
        }
    },
    async fetchCities({ state }, stateId) {
        try {
            const res = await http.get(`/misc/cities?state_id=${stateId}`)
            state.cities = res.data.result
        } catch (err) {
            console.log("error in loading dashboard stats", err)
        }
    },
    async fetchCountriesStatesCities({ state }) {
        try {
            const res = await http.get("/misc/countries-states-cities")
            const resData = res.data.result
            state.countries = resData.countries
            state.states = resData.states
            state.cities = resData.cities
        } catch (err) {
            console.log("error in loading dashboard stats", err)
        }
    },
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
}
